// Refactor 02/2026 — extrahiert aus AppointmentsPage.js (Phase 5, Iter 333).
// Lösch-Aktionen mit optimistic updates und Rollback:
//   - handleDeleteAppointment (optimistic delete + scope this/future,
//                              rollback bei Server-Fehler)
//   - confirmDelete           (klassisches Delete via Confirm-Dialog)
//   - handleRemoveParticipant (Teilnehmer aus Termin entfernen, mit Scope)

import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useDeleteActions = ({
  appointments,
  setAppointments,
  selectedAppointment,
  setSelectedAppointment,
  setContextMenu,
  setDeleteDialogOpen,
  setDeleteOptionsDialogOpen,
  fetchAppointments,
}) => {
  const handleDeleteAppointment = async (deleteScope = 'this', notifyCustomer = true, reason = '') => {
    const apptIdToDelete = selectedAppointment?.id;
    if (!apptIdToDelete) {
      toast.error('Kein Termin ausgewählt');
      return;
    }
    // Optimistic update: Termin sofort aus UI entfernen
    const previousAppointments = appointments;
    setAppointments((prev) => prev.filter((a) => a.id !== apptIdToDelete));
    setContextMenu(null);
    setSelectedAppointment(null);
    setDeleteOptionsDialogOpen(false);

    try {
      console.log(
        `Deleting appointment ${apptIdToDelete} with scope: ${deleteScope}, notify: ${notifyCustomer}`
      );
      const _reasonQs = reason && reason.trim() ? `&reason=${encodeURIComponent(reason.trim())}` : '';
      const url = `${API}/appointments/${apptIdToDelete}?delete_scope=${deleteScope}&notify_customer=${notifyCustomer ? 'true' : 'false'}${_reasonQs}`;
      const response = await axios.delete(url);
      console.log('Delete response:', response.data);

      if (deleteScope === 'future') {
        const count =
          response.data.deleted_count || response.data.removed_count || 0;
        if (response.data?.series_id) {
          const { addSeriesJob } = await import('@/lib/seriesJobsStore');
          addSeriesJob(response.data.series_id);
        }
        toast.success(
          `${count} Termine gelöscht/entfernt (dieser + zukünftige)!`
        );
      } else {
        toast.success('Termin gelöscht!');
      }
      // FLEX-Termine sind im State visuell gruppiert. Bei einem sekundären
      // Teilnehmer entspricht dessen echte Appointment-ID nicht der Gruppen-ID;
      // ein reines optimistic filter() kann die Kachel deshalb nicht entfernen.
      // Nach JEDEM Delete die bestätigte Serveransicht neu laden.
      await fetchAppointments();
    } catch (error) {
      console.error('Delete error:', error);
      // Rollback optimistic update on error
      setAppointments(previousAppointments);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;

      if (status === 400 && typeof detail === 'string') {
        toast.error(detail, { duration: 8000 });
      } else if (status === 404 && detail === 'Appointment not found') {
        toast.error('Termin wurde nicht gefunden. Die Ansicht wird aktualisiert.');
        fetchAppointments();
      } else {
        toast.error(detail || 'Fehler beim Löschen');
      }
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${API}/appointments/${selectedAppointment.id}?delete_scope=this`
      );
      toast.success('Termin gelöscht!');
      setDeleteDialogOpen(false);
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleRemoveParticipant = async (
    appointmentId,
    customerIdToRemove,
    removeScope = 'this'
  ) => {
    try {
      const response = await axios.post(
        `${API}/appointments/${appointmentId}/remove-participant?remove_scope=${removeScope}`,
        { customer_id: customerIdToRemove }
      );

      if (removeScope === 'future' && response.data.removed_count) {
        toast.success(
          `Teilnehmer aus ${response.data.removed_count + (response.data.deleted_count || 0)} Terminen entfernt!`
        );
      } else {
        toast.success('Teilnehmer entfernt!');
      }
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Entfernen');
    }
  };

  return {
    handleDeleteAppointment,
    confirmDelete,
    handleRemoveParticipant,
  };
};

export default useDeleteActions;
